/**
 * Offline storage module — IndexedDB for drafts, sync queue, and pending photos.
 *
 * Uses raw IndexedDB API (no heavy libraries) to keep bundle small.
 * Database: 'checkup-offline', version 1
 *
 * Object stores:
 *   - 'drafts': checkup session drafts, keyed by sessionId
 *   - 'sync-queue': pending API calls to replay, auto-increment key
 *   - 'photos-pending': photos waiting for upload, auto-increment key
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DraftEntry {
  sessionId: number;
  clientId: number;
  sessionType: string;
  answers: Record<string, unknown>;
  measurements?: Record<string, number | null>;
  updatedAt: number; // timestamp ms
  synced: boolean;
}

export interface SyncQueueEntry {
  id?: number;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  createdAt: number;
  retries: number;
  maxRetries: number;
}

export interface PendingPhoto {
  id?: number;
  sessionId: number;
  clientId: number;
  photoType: string;
  blob: Blob;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DB_NAME = 'checkup-offline';
const DB_VERSION = 1;

const STORE_DRAFTS = 'drafts';
const STORE_SYNC_QUEUE = 'sync-queue';
const STORE_PHOTOS = 'photos-pending';

// ---------------------------------------------------------------------------
// Database singleton
// ---------------------------------------------------------------------------

let _db: IDBDatabase | null = null;
let _dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Opens (or returns the cached) IndexedDB database.
 * Creates/upgrades object stores on first run.
 */
export function openDB(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      // Drafts — keyed by sessionId (one draft per checkup session)
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        const draftsStore = db.createObjectStore(STORE_DRAFTS, {
          keyPath: 'sessionId',
        });
        draftsStore.createIndex('synced', 'synced', { unique: false });
      }

      // Sync queue — auto-increment id, ordered FIFO
      if (!db.objectStoreNames.contains(STORE_SYNC_QUEUE)) {
        db.createObjectStore(STORE_SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true,
        });
      }

      // Pending photos — auto-increment id
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const photosStore = db.createObjectStore(STORE_PHOTOS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        photosStore.createIndex('sessionId', 'sessionId', { unique: false });
      }
    };

    request.onsuccess = () => {
      _db = request.result;

      // Handle unexpected close (e.g. storage quota exceeded)
      _db.onclose = () => {
        _db = null;
        _dbPromise = null;
      };

      resolve(_db);
    };

    request.onerror = () => {
      _dbPromise = null;
      reject(new Error(`IndexedDB open failed: ${request.error?.message}`));
    };
  });

  return _dbPromise;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Wraps an IDBRequest in a Promise.
 */
function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Wraps an IDBTransaction completion in a Promise.
 */
function txComplete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

/**
 * Save or update a checkup draft. Uses put() so it overwrites existing entries
 * for the same sessionId.
 */
export async function saveDraft(entry: DraftEntry): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_DRAFTS, 'readwrite');
  tx.objectStore(STORE_DRAFTS).put(entry);
  await txComplete(tx);
}

/**
 * Get a draft by session ID, or null if none exists.
 */
export async function getDraft(
  sessionId: number,
): Promise<DraftEntry | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_DRAFTS, 'readonly');
  const result = await promisify(
    tx.objectStore(STORE_DRAFTS).get(sessionId),
  );
  return (result as DraftEntry) ?? null;
}

/**
 * Get all unsynced drafts (synced !== true).
 *
 * IndexedDB non supporta boolean come key per gli index — IDBKeyRange.only(false)
 * lancia sempre DataError. Quindi prendiamo tutti i draft e filtriamo in memoria.
 * I draft per ogni cliente sono pochi (1 attivo + storia recente), il cost e' trascurabile.
 */
export async function getAllDrafts(): Promise<DraftEntry[]> {
  const db = await openDB();
  const tx = db.transaction(STORE_DRAFTS, 'readonly');
  const all = await promisify(tx.objectStore(STORE_DRAFTS).getAll());
  return (all as DraftEntry[]).filter((d) => !d.synced);
}

/**
 * Delete a draft after successful sync.
 */
export async function deleteDraft(sessionId: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_DRAFTS, 'readwrite');
  tx.objectStore(STORE_DRAFTS).delete(sessionId);
  await txComplete(tx);
}

// ---------------------------------------------------------------------------
// Sync Queue
// ---------------------------------------------------------------------------

/**
 * Add a pending API call to the sync queue.
 */
export async function addToSyncQueue(
  entry: Omit<SyncQueueEntry, 'id'>,
): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(STORE_SYNC_QUEUE, 'readwrite');
  const id = await promisify(
    tx.objectStore(STORE_SYNC_QUEUE).add(entry),
  );
  await txComplete(tx);
  return id as number;
}

/**
 * Get the oldest unprocessed sync queue item (lowest id = oldest, FIFO).
 * Returns null if queue is empty.
 */
export async function getNextSyncItem(): Promise<SyncQueueEntry | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_SYNC_QUEUE, 'readonly');
  const store = tx.objectStore(STORE_SYNC_QUEUE);

  return new Promise((resolve, reject) => {
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        resolve(cursor.value as SyncQueueEntry);
      } else {
        resolve(null);
      }
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

/**
 * Remove a sync queue item after successful processing.
 */
export async function removeSyncItem(id: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_SYNC_QUEUE, 'readwrite');
  tx.objectStore(STORE_SYNC_QUEUE).delete(id);
  await txComplete(tx);
}

/**
 * Increment retry count for a failed sync item.
 */
export async function incrementRetries(id: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_SYNC_QUEUE, 'readwrite');
  const store = tx.objectStore(STORE_SYNC_QUEUE);
  const item = await promisify(store.get(id));

  if (item) {
    const updated = item as SyncQueueEntry;
    updated.retries += 1;
    store.put(updated);
    await txComplete(tx);
  }
}

/**
 * Count total items in the sync queue.
 */
export async function getSyncQueueCount(): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(STORE_SYNC_QUEUE, 'readonly');
  return promisify(tx.objectStore(STORE_SYNC_QUEUE).count());
}

// ---------------------------------------------------------------------------
// Pending Photos
// ---------------------------------------------------------------------------

/**
 * Queue a photo for later upload.
 */
export async function addPendingPhoto(
  photo: Omit<PendingPhoto, 'id'>,
): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(STORE_PHOTOS, 'readwrite');
  const id = await promisify(
    tx.objectStore(STORE_PHOTOS).add(photo),
  );
  await txComplete(tx);
  return id as number;
}

/**
 * Get the oldest pending photo (FIFO).
 */
export async function getNextPendingPhoto(): Promise<PendingPhoto | null> {
  const db = await openDB();
  const tx = db.transaction(STORE_PHOTOS, 'readonly');
  const store = tx.objectStore(STORE_PHOTOS);

  return new Promise((resolve, reject) => {
    const cursorReq = store.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (cursor) {
        resolve(cursor.value as PendingPhoto);
      } else {
        resolve(null);
      }
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

/**
 * Remove a pending photo after successful upload.
 */
export async function removePendingPhoto(id: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE_PHOTOS, 'readwrite');
  tx.objectStore(STORE_PHOTOS).delete(id);
  await txComplete(tx);
}

/**
 * Count total pending photos.
 */
export async function getPendingPhotoCount(): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(STORE_PHOTOS, 'readonly');
  return promisify(tx.objectStore(STORE_PHOTOS).count());
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

/**
 * Wipe all offline data. Called on logout.
 */
export async function clearAll(): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(
    [STORE_DRAFTS, STORE_SYNC_QUEUE, STORE_PHOTOS],
    'readwrite',
  );
  tx.objectStore(STORE_DRAFTS).clear();
  tx.objectStore(STORE_SYNC_QUEUE).clear();
  tx.objectStore(STORE_PHOTOS).clear();
  await txComplete(tx);
}

/**
 * Count total pending items (sync queue + pending photos + unsynced drafts).
 */
export async function getTotalPendingCount(): Promise<number> {
  const db = await openDB();
  const tx = db.transaction(
    [STORE_DRAFTS, STORE_SYNC_QUEUE, STORE_PHOTOS],
    'readonly',
  );
  const [syncCount, photoCount, drafts] = await Promise.all([
    promisify(tx.objectStore(STORE_SYNC_QUEUE).count()),
    promisify(tx.objectStore(STORE_PHOTOS).count()),
    promisify(
      tx.objectStore(STORE_DRAFTS).index('synced').count(IDBKeyRange.only(false)),
    ),
  ]);
  return syncCount + photoCount + drafts;
}
